import { MINOR_UNIT_EXPONENT, type Currency } from './ledger';

/**
 * Money for display.
 *
 * Every number in the product is set in Martian Mono with tabular figures
 * (section 7.2); this only produces the string. Grouping and symbol placement
 * come from Intl so a Polish buyer sees a Polish-shaped price.
 */
export function formatMoney(currency: Currency, amountMinor: number, locale = 'en-GB'): string {
  const exponent = MINOR_UNIT_EXPONENT[currency];
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(amountMinor / 10 ** exponent);
}

/** Integers with grouping. Slot counts, review counts, players online. */
export function formatCount(value: number, locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * A percentage with its denominator attached.
 *
 * Hard rule 11: every displayed statistic carries its denominator. Never
 * "86% positive"; always "86% from 74 reviews". This function cannot produce
 * the first form, which is the point of it existing.
 */
export function formatShareWithDenominator(
  numerator: number,
  denominator: number,
  noun: string,
  locale = 'en-GB',
): string {
  if (denominator === 0) return `no ${noun} yet`;
  const share = Math.round((numerator / denominator) * 100);
  const plural = denominator === 1 ? noun.replace(/s$/, '') : noun;
  return `${share}% from ${formatCount(denominator, locale)} ${plural}`;
}

/**
 * Bytes, at the precision somebody actually reads.
 *
 * Binary units under decimal names, which is the convention every games
 * platform uses and every storage vendor argues with: a build the studio portal
 * calls 4.3 GB is 4.3 × 1024³ bytes. That is deliberate and worth stating,
 * because the alternative — being technically right with GiB — makes a download
 * size disagree with the number the operating system will show for the same
 * folder, and a player comparing the two has no way to know which is lying.
 *
 * One decimal below ten and none above it. `4.3 GB` is a number somebody
 * decides on; `4.28 GB` is a number nobody reads, and `487.3 MB` is precision
 * about a figure that will be wrong by more than that as soon as the build
 * changes.
 *
 * Lived in `apps/app/src/routes/studio.tsx` as a private helper until the
 * launcher's download screen needed the same string. Two copies of a size
 * formatter is how a build listed at 4.3 GB in the portal becomes 4.4 GB in the
 * launcher for no reason anybody can find.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;

  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}

/**
 * A date for display.
 *
 * **It takes a string as well as a `Date`, and that is a fix rather than a
 * convenience.** Every caller of this in the React app renders something that
 * arrived over HTTP, and the types said `Date` because core's own functions
 * return one — but JSON has no date type, so what actually crosses the wire is
 * an ISO string and `Intl.DateTimeFormat.prototype.format` throws
 * `RangeError: Invalid time value` when handed one. TypeScript could not see
 * it: the API's response types are core's return types, so the string is a
 * `Date` all the way to the point where it is not.
 *
 * The failure that produces is total rather than local. It throws during
 * render, React unmounts the tree, and the orders screen goes blank with the
 * whole error in the console and nothing on the page — which is why the
 * workaround that grew up around it was `formatDate(new Date(x))` at some call
 * sites and a plain `formatDate(x)` at others, in the same file. Accepting all
 * three shapes here means the wrapping is optional rather than load-bearing.
 *
 * An unparseable value returns an em dash instead of throwing. A missing date
 * is a hole in one cell; a thrown one is a blank screen, and no date is worth
 * the second.
 */
export function formatDate(value: Date | string | number, locale = 'en-GB'): string {
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(at);
}

/** The units a rough elapsed time is expressed in, largest first. */
const ELAPSED_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 86_400_000],
  ['month', 30 * 86_400_000],
  ['week', 7 * 86_400_000],
  ['day', 86_400_000],
  ['hour', 3_600_000],
  ['minute', 60_000],
];

/**
 * How long ago something happened, in words.
 *
 * Rounded **down** to the largest unit that fits, so it always understates: 59
 * minutes is "59 minutes ago" and never "an hour ago", and 13 days is "1 week
 * ago" rather than "2 weeks ago". A relative time that rounds up says something
 * happened longer ago than it did, and on a join log that reads as a server
 * that has gone quiet.
 *
 * Anything inside a minute is "just now" rather than "0 minutes ago" or a
 * seconds count. The page it feeds is cached at the edge for thirty seconds, so
 * a figure in seconds would be visibly wrong to anybody who watched it, and
 * pretending to that resolution would be a claim the cache cannot support.
 *
 * A future instant — a clock skew between Discord and this Worker — also lands
 * on "just now" rather than "in 3 minutes". Discord's `joined_at` is authoritative
 * and the skew is ours.
 */
export function formatElapsed(from: Date, now: Date = new Date(), locale = 'en-GB'): string {
  const elapsed = now.getTime() - from.getTime();
  if (!Number.isFinite(elapsed) || elapsed < 60_000) return 'just now';

  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });

  for (const [unit, size] of ELAPSED_UNITS) {
    const count = Math.floor(elapsed / size);
    if (count >= 1) return format.format(-count, unit);
  }

  return 'just now';
}
